import clsx from 'clsx';
import React from 'react';
import { Button as ReactAriaButton, Heading as ReactAriaHeading } from 'react-aria-components';

import { MaterialIcon } from '#/shared/components/MaterialIcon';
import { focusRingClassName } from '#/shared/utils/domHelper';

type ModalContentProps = {
  title: string;
  onCancel: () => void;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  children?: React.ReactNode;
};

// A container that conditionally renders as a form or a div
// based on the presence of onSubmit prop.

// This was done to support the scroll functionality to work in a way
// that the top and the bottom of the form stay fixed
// while the middle part scrolls when the content overflows.

// To do that, both the container which scrolls and the container with the buttons
// need to be direct children of the ModalContent component.
// However, we need the "form" element to wrap the buttons as well
// hence the "form" should wrap everything.",

// Delete once we don't need forms in the modals anymore.

const ModalContentContainer = ({
  onSubmit,
  children,
}: {
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  children?: React.ReactNode;
}) => {
  const className = 'h-full relative flex items-center';
  if (onSubmit) {
    return (
      <form className={className} onSubmit={onSubmit}>
        {children}
      </form>
    );
  }
  return <div className={className}>{children}</div>;
};

export function ModalContent({ title, onCancel, onSubmit, children }: Readonly<ModalContentProps>) {
  return (
    <ModalContentContainer onSubmit={onSubmit}>
      {/* 'py-24' for parent */}
      <div className="bg-surface-l0 rounded-md gap-16 flex flex-col w-full max-h-full py-24">
        <div className="relative px-24">
          <ReactAriaHeading slot="title" className="font-600 text-22 text-on-surface">
            {title}
          </ReactAriaHeading>
          <ReactAriaButton
            onPress={onCancel}
            className={clsx(
              'flex absolute top-0 right-24 p-4 rounded-xs',
              'text-primary',
              'hover:text-primary-shade hover:cursor-pointer',
              `focus:text-primary-shade ${focusRingClassName}`,
            )}
            aria-label={'Close'}
          >
            <MaterialIcon iconName="close" size="md" />
          </ReactAriaButton>
        </div>

        {/* add 'px-24' to child containers to create space for field outlines and scroll bar */}
        {/* add 'overflow-y-auto' to the child container you'd like to vertically scroll */}
        {children}
      </div>
    </ModalContentContainer>
  );
}
